import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { AzureBlobService } from '../../services/azure-blob.service';
import { FormAudioRecorderComponent, AudioRecording } from './form-audio-recorder.component';
import { FileUploadService } from '../../services/file-upload.service';

export interface RadioOption {
  value: string;
  label: string;
  color?: string;
  iconClass?: string;
}

export interface Photo {
  fileName: string;
  fileUrl: string;
}

export interface ConditionalInputsConfig {
  triggerValues: string[];           // 觸發額外輸入的選項值陣列
  enableRemarks: boolean;            // 是否啟用備註功能
  enableFileUpload: boolean;         // 是否啟用檔案上傳功能
  enableAudioRecorder: boolean;      // 是否啟用錄音功能
  remarksLabel?: string;             // 備註標籤文字（可選）
  fileUploadLabel?: string;          // 檔案上傳標籤文字（可選）
  audioRecorderLabel?: string;       // 錄音標籤文字（可選）
}

export interface RadioButtonValue {
  selectedValue: string;              // 選擇的選項值
  hasConditionalInputs: boolean;      // 是否有條件式輸入
  conditionalData?: {                 // 條件式輸入資料（可選）
    remarks?: string;                 // 備註內容
    uploadedFiles?: Photo[];          // 上傳的檔案
    audioRecording?: AudioRecording;  // 錄音資料
  };
}

@Component({
  selector: 'app-form-radiobutton',
  standalone: true,
  imports: [CommonModule, FormsModule, FormAudioRecorderComponent],
  template: `
    <div class="form-section">
      <!-- 標籤文字 -->
      <label class="question-label" [class.required-field]="required">{{ label }}</label>

      <!-- 只讀模式顯示 -->
      <div *ngIf="readonly" class="readonly-value">
        {{ getSelectedOptionLabel() }}
      </div>

      <!-- 編輯模式 -->
      <div *ngIf="!readonly">
        <!-- 提示文字（修正顯示） -->
        <div class="placeholder-text" *ngIf="placeholder && placeholder.trim()">
          {{ placeholder }}
        </div>

      <!-- 題目圖片（新功能） -->
      <div class="question-image-container" *ngIf="questionImageUrl">
        <img [src]="questionImageUrl"
             [alt]="questionImageAltText || '題目說明圖片'"
             (error)="onQuestionImageError($event)"
             (click)="onQuestionImageClick(questionImageUrl)"
             class="question-image">
      </div>

      <!-- 選項按鈕 -->
      <div class="button-group">
        <button
          *ngFor="let option of options"
          type="button"
          [ngClass]="[
            'btn-' + (option.color || 'blue'),
            value === option.value ? 'selected' : ''
          ]"
          (click)="onOptionClick(option.value)"
        >
          <i *ngIf="option.iconClass" [class]="option.iconClass"></i>
          {{ option.label }}
        </button>
      </div>

      <!-- 條件式操作按鈕 -->
      <div class="conditional-actions" *ngIf="shouldShowExtraInputs()">
        <button
          *ngIf="conditionalInputs?.enableRemarks"
          type="button"
          class="action-btn remarks-btn"
          [class.active]="remarkActive"
          (click)="toggleRemarks()"
        >
          📝 {{ conditionalInputs?.remarksLabel || '備註' }}
        </button>

        <button
          *ngIf="conditionalInputs?.enableFileUpload"
          type="button"
          class="action-btn upload-btn"
          [class.active]="fileUploadActive"
          (click)="toggleFileUpload()"
        >
          📷 {{ conditionalInputs?.fileUploadLabel || '上傳照片' }}
        </button>

        <button
          *ngIf="conditionalInputs?.enableAudioRecorder"
          type="button"
          class="action-btn audio-recorder-btn"
          [class.active]="audioRecorderActive"
          (click)="toggleAudioRecorder()"
        >
          🎤 {{ conditionalInputs?.audioRecorderLabel || '錄音' }}
        </button>
      </div>

      <!-- 條件式輸入區域 -->
      <div class="conditional-inputs" *ngIf="shouldShowExtraInputs()">
        <!-- 備註區域 -->
        <div class="remarks-section" *ngIf="conditionalInputs?.enableRemarks && remarkActive">
          <div class="input-section">
            <label class="input-label">{{ conditionalInputs?.remarksLabel || '備註' }}</label>
            <textarea
              [(ngModel)]="remarks"
              (ngModelChange)="onRemarksChange()"
              placeholder="請輸入備註..."
              class="form-input remarks-textarea"
            ></textarea>
          </div>
        </div>

        <!-- 檔案上傳區域 -->
        <div class="file-upload-section" *ngIf="conditionalInputs?.enableFileUpload && fileUploadActive">
          <div class="input-section">
            <label class="input-label">{{ conditionalInputs?.fileUploadLabel || '上傳照片' }}</label>
            <input
              type="file"
              (change)="handlePhotoUpload($event)"
              accept="image/*"
              multiple
              class="file-input"
            />
            <div class="photo-preview" *ngIf="uploadedFiles.length > 0">
              <div *ngFor="let photo of uploadedFiles; let i = index" class="photo-item">
                <img [src]="photo.fileUrl" alt="Preview" />
                <button type="button" (click)="removePhoto(i)" class="remove-photo-btn">X</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 錄音區域 -->
        <div class="audio-recorder-section" *ngIf="conditionalInputs?.enableAudioRecorder && audioRecorderActive">
          <div class="input-section">
            <label class="input-label">{{ conditionalInputs?.audioRecorderLabel || '錄音' }}</label>
            <app-form-audio-recorder
              [(ngModel)]="audioRecording"
              (ngModelChange)="onAudioRecordingChange($event)"
              [label]="''"
              [maxDuration]="300"
              [minDuration]="1"
              [audioQuality]="'medium'"
              [audioFormat]="'wav'">
            </app-form-audio-recorder>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormRadiobuttonComponent),
      multi: true,
    },
  ],
  styleUrls: ['total-component.scss'],
})
export class FormRadiobuttonComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = '';
  @Input() placeholder: string = '';                // 提示文字
  @Input() options: RadioOption[] = [];
  @Input() name: string = '';                       // 新增 name 輸入屬性
  @Input() id: string = '';                         // 新增 id 輸入屬性
  @Input() required: boolean = false;               // 是否必填
  @Input() readonly: boolean = false;               // 新增：只讀模式
  @Input() conditionalInputs: ConditionalInputsConfig | null = null; // 條件式輸入配置

  // 新增題目圖片相關屬性
  @Input() questionImageUrl?: string;               // 題目圖片 URL
  @Input() questionImageBlobName?: string;          // Azure Blob 名稱（用於管理）
  @Input() questionImageAltText?: string;           // 圖片替代文字（無障礙）

  @Output() clearRemarks = new EventEmitter<void>();

  value: string = '';

  // 條件式輸入狀態管理
  remarkActive: boolean = false;
  fileUploadActive: boolean = false;
  audioRecorderActive: boolean = false;
  remarks: string = '';
  uploadedFiles: Photo[] = [];
  audioRecording: AudioRecording | null = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private azureBlobService: AzureBlobService, private fileUploadService: FileUploadService) {}

  ngOnInit(): void {
    // 初始化時產生題目圖片 URL
    this.generateQuestionImageUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 questionImageBlobName 變更時，重新產生圖片 URL
    if (changes['questionImageBlobName'] && changes['questionImageBlobName'].currentValue) {
      this.generateQuestionImageUrl();
    }
  }

  // 產生題目圖片預覽 URL
  private generateQuestionImageUrl(): void {
    if (this.questionImageBlobName && this.questionImageBlobName.trim()) {
      this.fileUploadService.generateViewUrl(this.questionImageBlobName).subscribe({
        next: (response) => {
          if (response && response.data.viewUrl) {
            this.questionImageUrl = response.data.viewUrl;
            console.log('題目圖片 URL 已產生:', this.questionImageUrl);
          } else {
            console.warn('產生題目圖片 URL 回應異常:', response);
          }
        },
        error: (error) => {
          console.error('產生題目圖片 URL 失敗:', error);
          this.questionImageUrl = undefined;
        }
      });
    } else {
      // 如果沒有 blobName，清空 URL
      this.questionImageUrl = undefined;
    }
  }

  // 處理圖片載入錯誤
  onQuestionImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    console.warn('題目圖片載入失敗:', this.questionImageUrl);
  }

  // 處理圖片點擊（可選：放大檢視）
  onQuestionImageClick(imageUrl: string): void {
    // 可以實作圖片放大檢視功能
    console.log('圖片點擊:', imageUrl);
  }

  onOptionClick(selectedValue: string): void {
    this.value = selectedValue;

    // 如果切換到不同選項，重置條件式輸入狀態
    if (!this.shouldShowExtraInputs()) {
      this.resetConditionalInputs();
    }

    this.emitValue();
    this.clearRemarks.emit();
  }

  // 判斷是否應該顯示額外輸入功能
  shouldShowExtraInputs(): boolean {
    return !!(this.conditionalInputs &&
              this.conditionalInputs.triggerValues.includes(this.value));
  }

  // 重置條件式輸入狀態
  resetConditionalInputs(): void {
    this.remarkActive = false;
    this.fileUploadActive = false;
    this.audioRecorderActive = false;
    this.remarks = '';
    this.uploadedFiles = [];
    this.audioRecording = null;
  }

  // 切換備註輸入狀態
  toggleRemarks(): void {
    this.remarkActive = !this.remarkActive;
    if (!this.remarkActive) {
      this.remarks = '';
    }
    this.emitValue();
  }

  // 切換檔案上傳狀態
  toggleFileUpload(): void {
    this.fileUploadActive = !this.fileUploadActive;
    if (!this.fileUploadActive) {
      this.uploadedFiles = [];
    }
    this.emitValue();
  }

  // 切換錄音狀態
  toggleAudioRecorder(): void {
    this.audioRecorderActive = !this.audioRecorderActive;
    if (!this.audioRecorderActive) {
      this.audioRecording = null;
    }
    this.emitValue();
  }

  // 處理備註變更
  onRemarksChange(): void {
    this.emitValue();
  }

  // 處理錄音變更
  onAudioRecordingChange(recording: AudioRecording | null): void {
    this.audioRecording = recording;
    this.emitValue();
  }

  // 處理檔案上傳
  handlePhotoUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      files.forEach((file) => {
        // Upload file to Azure Blob Storage
        this.azureBlobService.uploadFile(file, 'YourCompanyName', 'fileupload')
          .then((fileUrl: string) => {
            const photoItem: Photo = {
              fileName: file.name,
              fileUrl: fileUrl  // Azure URL after upload
            };
            this.uploadedFiles.push(photoItem);  // Add to photo array
            this.emitValue();
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
          });
      });
    }
  }

  // 移除照片
  removePhoto(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.emitValue();
  }

  // 發送更新後的值
  emitValue(): void {
    const hasConditionalInputs = this.shouldShowExtraInputs();

    let outputValue: string | RadioButtonValue = this.value;

    // 如果有條件式輸入且有實際的條件式資料，輸出完整的資料結構
    if (hasConditionalInputs) {
      const hasRemarks = this.remarkActive && this.remarks.trim();
      const hasFiles = this.fileUploadActive && this.uploadedFiles.length > 0;
      const hasAudio = this.audioRecorderActive && this.audioRecording;

      // 只有當真的有條件式資料時才輸出完整結構
      if (hasRemarks || hasFiles || hasAudio) {
        const conditionalData: any = {};
        if (hasRemarks) conditionalData.remarks = this.remarks;
        if (hasFiles) conditionalData.uploadedFiles = this.uploadedFiles;
        if (hasAudio && this.audioRecording) conditionalData.audioRecording = this.audioRecording;

        outputValue = {
          selectedValue: this.value,
          hasConditionalInputs: true,
          conditionalData
        };
      } else {
        // 即使是觸發值，但沒有實際的條件式資料時，仍然輸出簡單的字串
        outputValue = this.value;
      }
    }

    this.onChange(outputValue);
  }

  writeValue(value: string | RadioButtonValue): void {
    if (typeof value === 'string') {
      this.value = value;
      // 當從外部設定簡單字串值時，重置條件式輸入狀態
      this.resetConditionalInputs();
    } else if (value && typeof value === 'object') {
      this.value = value.selectedValue;
      if (value.conditionalData) {
        this.remarks = value.conditionalData.remarks || '';
        this.uploadedFiles = value.conditionalData.uploadedFiles || [];
        this.audioRecording = value.conditionalData.audioRecording || null;
        this.remarkActive = !!value.conditionalData.remarks;
        this.fileUploadActive = !!(value.conditionalData.uploadedFiles && value.conditionalData.uploadedFiles.length > 0);
        this.audioRecorderActive = !!value.conditionalData.audioRecording;
      } else {
        // 如果沒有條件式資料，重置狀態
        this.resetConditionalInputs();
      }
    } else if (value === null || value === undefined) {
      // 處理空值情況
      this.value = '';
      this.resetConditionalInputs();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  // 新增：取得選中選項的顯示文字（用於只讀模式）
  getSelectedOptionLabel(): string {
    if (!this.value) return '-';
    
    // 嘗試根據值找到對應的選項
    const selectedOption = this.options.find(option => option.value === this.value);
    if (selectedOption) {
      return selectedOption.label;
    }
    
    // 如果找不到對應的選項，返回值本身
    return this.value || '-';
  }
}
